import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  Grid,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { SupportMaterialFile, UpdateSupportMaterialRequest } from './types';
import { SupportMaterialService } from './supportMaterialService';

interface EditFileModalProps {
  open: boolean;
  file: SupportMaterialFile;
  onClose: () => void;
  onSave: () => void;
  onNotify: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

export const EditFileModal: React.FC<EditFileModalProps> = ({
  open,
  file,
  onClose,
  onSave,
  onNotify,
}) => {
  const [description, setDescription] = useState('');
  const [orderPosition, setOrderPosition] = useState<number | ''>('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Cargar datos del archivo cuando se abre el modal
  useEffect(() => {
    if (open && file) {
      setDescription(file.description || '');
      setOrderPosition(file.orderPosition || '');
      setActive(file.active);
    }
  }, [open, file]);

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const username = localStorage.getItem('username') || undefined;
      
      const updateRequest: UpdateSupportMaterialRequest = {
        description: description || undefined,
        orderPosition: orderPosition === '' ? undefined : Number(orderPosition),
        active,
        updatedBy: username,
      };

      await SupportMaterialService.updateFile(file.id, updateRequest);
      
      onNotify('Archivo actualizado exitosamente', 'success');
      onSave();
    } catch (error: any) {
      onNotify(error.message || 'Error al actualizar archivo', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Editar Archivo</Typography>
          <IconButton onClick={onClose} disabled={saving}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{file.fileName}</strong>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Carpeta: {file.folder} • Tipo: {file.fileType}
            </Typography>
          </Alert>
        </Box>

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción del archivo"
              disabled={saving}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Posición de orden"
              type="number"
              value={orderPosition}
              onChange={(e) => setOrderPosition(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="1, 2, 3..."
              disabled={saving}
              helperText="Define el orden de visualización"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={active ? 'Activo' : 'Inactivo'}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {active
                  ? 'El archivo está visible'
                  : 'El archivo está oculto'}
              </Typography>
            </Box>
          </Grid>

          {/* File metadata (readonly) */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Tamaño:</strong> {formatFileSize(file.sizeBytes)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Tipo de contenido:</strong> {file.contentType}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Creado:</strong> {new Date(file.createdAt).toLocaleString('es-MX')}{' '}
                por {file.createdBy}
              </Typography>
              {file.updatedAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Última actualización:</strong>{' '}
                  {new Date(file.updatedAt).toLocaleString('es-MX')}
                  {file.updatedBy && ` por ${file.updatedBy}`}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Existe en S3:</strong> {file.existsInS3 ? 'Sí ✓' : 'No ✗'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== HELPERS ====================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};
